import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import config from '../../config';
import { useRoomNames } from './useRoomNames';
import { isGstApplicable } from './gstSettings';
import { toast } from 'react-toastify';

/* ================= HELPER ================= */
const addOneDay = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

export default function RoomBookingWizard() {
  const { roomLabel } = useRoomNames();
  const [step, setStep] = useState(1);

  /* ================= STEP 1 ================= */
  const [hotels, setHotels] = useState([]);
  const [search, setSearch] = useState({
    hotelId: '',
    fromDate: '',
    toDate: '',
  });

  /* ================= STEP 2 ================= */
  const [rooms, setRooms] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  /* ================= STEP 3 ================= */
  const [guest, setGuest] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    Address: '',
    City: '',
    idProofType: '',
    idProofNumber: '',
  });
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const Companycurrency = company?.currency ?? '';
  const [amount, setAmount] = useState(0);

  const [payments, setPayments] = useState([
    { mode: '', amount: '', cardType: '', cardNumber: '', refNo: '' },
  ]);

  /* ================= AGENT ================= */
  const [bookingSource, setBookingSource] = useState('WalkIn');
  const [agents, setAgents] = useState([]);
  const [agentDetails, setAgentDetails] = useState({
    agentId: '',
    referenceNo: '',
    remark: '',
  });

  /* ================= UI STATE ================= */
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  /* ================= GST ================= */
  // Slab keys off the per-room-per-night GROSS price, NOT the whole-stay total.
  // `amount` is the total (all rooms × all nights), so divide first — else a
  // cheap room over several nights/rooms crosses 7875 and gets over-taxed.
  const _nights = Math.max(
    1,
    Math.round(
      (new Date(search.toDate) - new Date(search.fromDate)) / 86400000,
    ) || 1,
  );
  const _roomCount = selectedRooms?.length || 1;
  const _perNightGross = amount / _roomCount / _nights;
  // Company GST switch OFF → no GST at all; otherwise apply the slab.
  const gstRate = !isGstApplicable()
    ? 0
    : _perNightGross < 1000
      ? 0
      : _perNightGross < 7875
        ? 5
        : 18;
  // `amount` is the WITH-GST (gross) booking amount, so the GST is already
  // inside it — extract it with the inclusive formula, don't add it on top.
  // (amount * rate/100 wrongly treated amount as the base → over-stated GST.)
  const gstAmount = (amount * gstRate) / (100 + gstRate);
  const baseAmount = amount - gstAmount;

  /* ================= LOAD HOTELS ================= */
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    fetch(`${config.API_BASE_URL}/Home/GetHotelsdropdown/${company?.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setHotels(data || []))
      .catch((err) => console.error('Failed to load hotels:', err));
  }, []);

  /* ================= LOAD AGENTS ================= */
  useEffect(() => {
    if (bookingSource === 'Agent') {
      const token = sessionStorage.getItem('token');
      fetch(
        `${config.API_BASE_URL}/Home/GetallAgent/${encodeURIComponent(company?.id)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
        .then((res) => res.json())
        .then((data) => setAgents(data || []))
        .catch((err) => console.error('Failed to load agents:', err));
    } else {
      setAgents([]);
      setAgentDetails({ agentId: '', referenceNo: '', remark: '' });
    }
  }, [bookingSource]);

  /* ================= FETCH ROOMS ================= */
  const fetchRooms = async () => {
    const url = `${config.API_BASE_URL}/Home/Allavailablerooms/${search.hotelId}/${search.fromDate}/${search.toDate}/${company?.id}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : [];
    setRooms(data);
    setSelectedRooms([]);
    setSelectAll(false);
    setStep(2);
  };

  /* ================= ROOM SELECT ================= */
  const toggleRoom = (room) => {
    const exists = selectedRooms.find((r) => r.roomnumber === room.roomnumber);
    if (exists) {
      setSelectedRooms(
        selectedRooms.filter((r) => r.roomnumber !== room.roomnumber),
      );
    } else {
      setSelectedRooms([...selectedRooms, room]);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedRooms(
      checked ? rooms.filter((r) => r.status === 'Available') : [],
    );
  };

  /* ================= PAYMENTS ================= */
  const addPaymentRow = () => {
    setPayments([
      ...payments,
      { mode: '', amount: '', cardType: '', cardNumber: '', refNo: '' },
    ]);
  };

  const removePaymentRow = (index) => {
    if (payments.length > 1) {
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const updatePayment = (index, field, value) => {
    const copy = [...payments];
    copy[index][field] = value;
    setPayments(copy);
  };

  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  /* ================= CONFIRM BOOKING - FIXED ================= */
  // #14: validate booking (availability/guest/tariff/dates) WITHOUT booking.
  const handleCheckBooking = () => {
    const issues = [];
    if (!guest.firstName || !guest.lastName) issues.push('Guest name');
    if (!guest.mobile) issues.push('Mobile number');
    if (!guest.idProofType || !guest.idProofNumber) issues.push('ID proof');
    if (!(amount > 0)) issues.push('Booking amount');
    if (selectedRooms.length === 0) issues.push('Room selection');
    if (!search.fromDate || !search.toDate) issues.push('Check-in / Check-out date');
    else if (new Date(search.fromDate) >= new Date(search.toDate)) issues.push('Check-out must be after check-in');
    if (issues.length) { toast.error('Missing / invalid: ' + issues.join(', ')); return; }
    toast.success('All checks passed. Rooms ' + selectedRooms.map((r) => r.roomnumber).join(', ') + ' | ' + search.fromDate + ' to ' + search.toDate + ' | ' + guest.firstName + ' ' + guest.lastName + ' | Amount ' + amount);
  };

  const handleConfirmBooking = async () => {
    // Basic validation
    if (
      !guest.firstName ||
      !guest.lastName ||
      !guest.mobile ||
      !guest.idProofType ||
      !guest.idProofNumber
    ) {
      toast.error('Please fill all required guest details including ID Proof.');
      return;
    }
    if (amount <= 0) {
      toast.error('Please enter a valid booking amount.');
      return;
    }
    if (selectedRooms.length === 0) {
      toast.error('No rooms selected.');
      return;
    }

    setLoading(true);
    setSubmitStatus(null);
    setErrorMessage('');

    const payload = {
      hotelId: parseInt(search.hotelId),
      fromDate: search.fromDate,
      toDate: search.toDate,
      roomNumbers: selectedRooms.map((r) => r.roomnumber),

      // Guest
      title: guest.title || null,
      firstName: guest.firstName,
      lastName: guest.lastName,
      email: guest.email || null,
      mobile: guest.mobile,
      address: guest.Address,
      city: guest.City,

      bookingSource: bookingSource,
      agentId:
        bookingSource === 'Agent' ? parseInt(agentDetails.agentId) : null,
      agentReferenceNo:
        bookingSource === 'Agent' ? agentDetails.referenceNo || null : null,
      agentRemark:
        bookingSource === 'Agent' ? agentDetails.remark || null : null,

      // ID Proof
      idProofType: guest.idProofType,
      idProofNumber: guest.idProofNumber,

      // Amount
      totalAmount: parseFloat(amount),
      baseAmount: parseFloat(baseAmount.toFixed(2)),
      gstRate: gstRate,
      gstAmount: parseFloat(gstAmount.toFixed(2)),

      // Payments
      payments: payments
        .filter((p) => p.mode && p.amount > 0)
        .map((p) => ({
          mode: p.mode,
          amount: parseFloat(p.amount),
          cardType: p.cardType || null,
          cardNumber: p.cardNumber || null,
          referenceNo: p.refNo || null,
        })),
    };

    // Optional agent details
    if (bookingSource === 'Agent' && agentDetails.agentId) {
      payload.agentId = parseInt(agentDetails.agentId);
      payload.agentReferenceNo = agentDetails.referenceNo || null;
      payload.agentRemark = agentDetails.remark || null;
    }

    console.log('Sending payload:', payload); // Debug: Check payload

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${config.API_BASE_URL}/Home/CreateRoomBooking`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      // Always read response as text first to avoid JSON parse error
      const responseText = await response.text();

      console.log('Raw response:', responseText); // Debug: See exact response

      if (!response.ok) {
        // Handle HTTP errors (400, 404, 500, etc.)
        setSubmitStatus('error');
        setErrorMessage(
          `Server Error ${response.status}: ${responseText || 'No response from server'}`,
        );
        toast.error(
          `Booking failed: ${response.status} - ${responseText || 'Check console'}`,
        );
        return;
      }

      // Try to parse JSON only if there's content
      if (responseText.trim()) {
        try {
          const result = JSON.parse(responseText);
          setSubmitStatus('success');
          toast.success('Booking confirmed successfully!');
          window.location.reload();
          // Optional: reset form or redirect
        } catch (parseErr) {
          console.warn('Response is not valid JSON:', responseText);
          setSubmitStatus('success');
          toast.success(
            'Booking likely succeeded (server responded without JSON).',
          );
        }
      } else {
        // Empty response body (still 200 OK)
        console.warn('Empty response body but status OK');
        setSubmitStatus('success');
        toast.success('Booking confirmed (server accepted request).');
      }
    } catch (err) {
      console.error('Network or other error:', err);
      setSubmitStatus('error');
      setErrorMessage('Network error or server unreachable.');
      toast.error(
        'Failed to connect to server. Check your internet or API URL.',
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <Container maxWidth='md' sx={{ mt: 4, mb: 6 }}>
      <Typography variant='h5' textAlign='center' fontWeight='bold' mb={3}>
        Room Booking Wizard
      </Typography>

      {/* Success/Error Alert */}
      {submitStatus === 'success' && (
        <Alert severity='success' sx={{ mb: 2 }}>
          Booking created successfully!
        </Alert>
      )}
      {submitStatus === 'error' && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Failed to create booking: {errorMessage || 'Please try again.'}
        </Alert>
      )}

      {/* ================= STEP 1 ================= */}
      {step === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant='h6'>Select Hotel</Typography>
          <TextField
            select
            fullWidth
            label='Hotel'
            value={search.hotelId}
            onChange={(e) => setSearch({ ...search, hotelId: e.target.value })}>
            {hotels.map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {h.hotelName}
              </MenuItem>
            ))}
          </TextField>

          <Typography mt={3}>From Date</Typography>
          <TextField
            type='date'
            fullWidth
            value={search.fromDate}
            onChange={(e) => {
              const from = e.target.value;
              setSearch((prev) => ({
                ...prev,
                fromDate: from,
                toDate: prev.toDate || addOneDay(from),
              }));
            }}
          />

          <Typography mt={3}>To Date</Typography>
          <TextField
            type='date'
            fullWidth
            value={search.toDate}
            inputProps={{ min: search.fromDate }}
            onChange={(e) => setSearch({ ...search, toDate: e.target.value })}
          />

          <Button
            variant='contained'
            fullWidth
            sx={{ mt: 3 }}
            onClick={fetchRooms}>
            Search Rooms
          </Button>
        </Paper>
      )}

      {/* ================= STEP 2 ================= */}
      {step === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant='h6'>Select Rooms</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            }
            label='Select All Available Rooms'
          />

          <Grid container spacing={2}>
            {rooms.map((r) => {
              const selected = selectedRooms.some(
                (x) => x.roomnumber === r.roomnumber,
              );
              return (
                <Grid item xs={3} key={r.roomnumber}>
                  <Box
                    onClick={() => r.status === 'Available' && toggleRoom(r)}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 2,
                      bgcolor: selected
                        ? '#bbdefb'
                        : r.status === 'Available'
                          ? '#e8f5e9'
                          : '#ffebee',
                      border: `2px solid ${selected ? 'blue' : r.status === 'Available' ? 'green' : 'red'}`,
                    }}>
                    <b>{roomLabel(r.roomnumber)}</b>
                    <div>{r.roomTypeName}</div>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          <Button
            variant='contained'
            fullWidth
            sx={{ mt: 3 }}
            disabled={!selectedRooms.length}
            onClick={() => setStep(3)}>
            Continue ({selectedRooms.length} Rooms)
          </Button>
        </Paper>
      )}

      {/* ================= STEP 3 ================= */}
      {step === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant='h6'>Guest Details</Typography>
          <Typography color='primary' mt={1}>
            Selected Rooms:{' '}
            {selectedRooms.map((r) => roomLabel(r.roomnumber)).join(', ')}
          </Typography>

          {/* -------- AGENT -------- */}
          <Typography mt={3} fontWeight='bold'>
            Booking Source
          </Typography>
          <TextField
            select
            fullWidth
            label='Booking Source'
            value={bookingSource}
            onChange={(e) => setBookingSource(e.target.value)}>
            <MenuItem value='WalkIn'>Walk In</MenuItem>
            <MenuItem value='Agent'>Through Agent</MenuItem>
          </TextField>

          {bookingSource === 'Agent' && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <TextField
                select
                fullWidth
                label='Agent Name'
                value={agentDetails.agentId}
                onChange={(e) =>
                  setAgentDetails({ ...agentDetails, agentId: e.target.value })
                }>
                {agents.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label='Reference No'
                value={agentDetails.referenceNo}
                onChange={(e) =>
                  setAgentDetails({
                    ...agentDetails,
                    referenceNo: e.target.value,
                  })
                }
              />
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label='Remark'
                value={agentDetails.remark}
                onChange={(e) =>
                  setAgentDetails({ ...agentDetails, remark: e.target.value })
                }
              />
            </Paper>
          )}

          {/* -------- GUEST -------- */}
          <Typography mt={3}>Title</Typography>
          <TextField
            select
            fullWidth
            label='Title'
            value={guest.title}
            onChange={(e) => setGuest({ ...guest, title: e.target.value })}>
            <MenuItem value='Mr'>Mr</MenuItem>
            <MenuItem value='Mrs'>Mrs</MenuItem>
            <MenuItem value='Ms'>Ms</MenuItem>
            <MenuItem value='Dr'>Dr</MenuItem>
          </TextField>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='First Name'
                required
                value={guest.firstName}
                onChange={(e) =>
                  setGuest({ ...guest, firstName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Last Name'
                required
                value={guest.lastName}
                onChange={(e) =>
                  setGuest({ ...guest, lastName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Email'
                value={guest.email}
                onChange={(e) => setGuest({ ...guest, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Mobile'
                required
                value={guest.mobile}
                onChange={(e) => setGuest({ ...guest, mobile: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Address'
                required
                value={guest.Address}
                onChange={(e) =>
                  setGuest({ ...guest, Address: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='City'
                required
                value={guest.City}
                onChange={(e) => setGuest({ ...guest, City: e.target.value })}
              />
            </Grid>
          </Grid>

          {/* ======== ID PROOF DETAILS ======== */}
          <Typography mt={3} fontWeight='bold'>
            ID Proof Details
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label='ID Proof Type'
                value={guest.idProofType}
                onChange={(e) =>
                  setGuest({ ...guest, idProofType: e.target.value })
                }
                required>
                <MenuItem value='Aadhaar Card'>Aadhaar Card</MenuItem>
                <MenuItem value='Driving Licence'>Driving Licence</MenuItem>
                <MenuItem value='Voter ID Card'>Voter ID Card</MenuItem>
                <MenuItem value='Passport'>Passport</MenuItem>
                <MenuItem value='PAN Card'>PAN Card</MenuItem>
                <MenuItem value='Other'>Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='ID Number'
                value={guest.idProofNumber}
                onChange={(e) =>
                  setGuest({ ...guest, idProofNumber: e.target.value })
                }
                placeholder='Enter ID number'
                required
              />
            </Grid>
          </Grid>

          {/* -------- AMOUNT -------- */}
          <Typography mt={3} fontWeight='bold'>
            {isGstApplicable() ? 'Booking Amount (With GST)' : 'Booking Amount'}
          </Typography>
          <TextField
            type='number'
            fullWidth
            value={amount}
            onChange={(e) => setAmount(+e.target.value || 0)}
            inputProps={{ min: 0 }}
          />

          <Grid container spacing={2} mt={1}>
            <Grid item xs={isGstApplicable() ? 6 : 12}>
              <TextField
                label={isGstApplicable() ? 'Amount Without GST' : 'Amount'}
                fullWidth
                value={baseAmount.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            {isGstApplicable() && (
            <Grid item xs={6}>
              <TextField
                label={`GST Amount (${gstRate}%)`}
                fullWidth
                value={gstAmount.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            )}
          </Grid>

          {/* -------- PAYMENTS -------- */}
          <Typography mt={3} fontWeight='bold'>
            Payments
          </Typography>
          {payments.map((p, i) => (
            <Paper key={i} sx={{ p: 2, mt: 2 }}>
              <TextField
                select
                fullWidth
                label='Payment Mode'
                value={p.mode}
                onChange={(e) => updatePayment(i, 'mode', e.target.value)}>
                <MenuItem value='Cash'>Cash</MenuItem>
                <MenuItem value='Card'>Card</MenuItem>
                <MenuItem value='UPI'>UPI</MenuItem>
                <MenuItem value='Online'>Online</MenuItem>
              </TextField>

              <TextField
                type='number'
                fullWidth
                sx={{ mt: 2 }}
                label='Amount'
                value={p.amount}
                onChange={(e) => updatePayment(i, 'amount', e.target.value)}
              />

              <IconButton
                color='error'
                onClick={() => removePaymentRow(i)}
                disabled={payments.length === 1}>
                <DeleteIcon />
              </IconButton>

              {p.mode === 'Card' && (
                <>
                  <TextField
                    fullWidth
                    sx={{ mt: 2 }}
                    label='Card Type'
                    value={p.cardType}
                    onChange={(e) =>
                      updatePayment(i, 'cardType', e.target.value)
                    }
                  />
                  <TextField
                    fullWidth
                    sx={{ mt: 2 }}
                    label='Card Number'
                    value={p.cardNumber}
                    onChange={(e) =>
                      updatePayment(i, 'cardNumber', e.target.value)
                    }
                  />
                </>
              )}

              {(p.mode === 'UPI' || p.mode === 'Online') && (
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  label='Reference No'
                  value={p.refNo}
                  onChange={(e) => updatePayment(i, 'refNo', e.target.value)}
                />
              )}
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={addPaymentRow}>
            Add Payment
          </Button>

          <Typography mt={2} fontWeight='bold'>
            Total Paid: {Companycurrency} {totalPaid}
          </Typography>

          <Button
            variant='outlined'
            color='primary'
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleCheckBooking}>
            Check
          </Button>
          <Button
            variant='contained'
            color='success'
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleConfirmBooking}
            disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Confirm Booking'}
          </Button>
        </Paper>
      )}
    </Container>
  );
}
